'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';

interface RegisterFormNewProps {
  dict?: any;
  currentLang?: string;
}

export default function RegisterFormNew({ dict, currentLang = 'en' }: RegisterFormNewProps) {
  const router = useRouter();
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    organisationName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    // Validation
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setError('Please enter your full name');
      setLoading(false);
      return;
    }

    if (!formData.phoneNumber.trim()) {
      setError('Please enter your phone number');
      setLoading(false);
      return;
    }

    if (!formData.organisationName.trim()) {
      setError('Please enter your agency name');
      setLoading(false);
      return;
    }

    if (!formData.email.trim()) {
      setError('Please enter your email address');
      setLoading(false);
      return;
    }

    if (!formData.password.trim()) {
      setError('Please enter a password');
      setLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      const baseUrl = process.env.NEXT_PUBLIC_API_BACKEND || 'http://localhost:8000';
      const url = `${baseUrl}/api/v1/organisations/public/create-with-user`;

      const payload = {
        organisationName: formData.organisationName,
        email: formData.email,
        userEmail: formData.email,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phoneNumber: formData.phoneNumber,
      };

      console.log('🔍 [Register] Sending payload:', payload);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      console.log('🔍 [Register] API Response:', result);

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Registration failed');
      }

      setSuccess(true);
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        router.push(`/${currentLang}/login`);
      }, 2000);

    } catch (err) {
      console.error('❌ [Register] Error:', err);
      setError(err instanceof Error ? err.message : 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <section className="signup-sec">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 col-md-8 col-11 m-auto">
              <div className="signup-box text-center py-5">
                <div className="alert alert-success" role="alert">
                  <h3>Account Created Successfully!</h3>
                  <p>Redirecting to login page...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="signup-sec">
      <div className="container">
        <div className="row">
          <div className="col-lg-8 col-md-8 col-11 m-auto">
            <div className="signup-box">
              <h2>
                Sign <span>Up</span>
              </h2>

              {error && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                  {error}
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setError(null)}
                  ></button>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-lg-12">
                    <label>
                      Full Name <span>*</span>
                    </label>
                    <div className="row">
                      <div className="col-lg-6">
                        <input 
                          type="text" 
                          className="form-control mb-2" 
                          placeholder="First Name"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="col-lg-6">
                        <input 
                          type="text" 
                          className="form-control mb-2" 
                          placeholder="Last Name"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="des-text">
                      <p>Please enter your full name (e.g., John Doe or Jane Doe).</p>
                    </div>
                  </div>

                  <div className="col-lg-12">
                    <label>
                      Phone Number <span>*</span>
                    </label>
                    <input 
                      type="tel" 
                      className="form-control" 
                      placeholder="+1234567890"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      required
                    />
                    <div className="des-text">
                      <p>Please enter a phone number.</p>
                    </div>
                  </div>

                  <div className="col-lg-12">
                    <label>
                      Agency Name <span>*</span>
                    </label>
                    <input 
                      type="text" 
                      className="form-control" 
                      placeholder="Enter your agency/organization name"
                      name="organisationName"
                      value={formData.organisationName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="col-lg-12">
                    <label>
                      Email Address <span>*</span>
                    </label>
                    <input 
                      type="email" 
                      className="form-control" 
                      placeholder="Enter your email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                    <div className="des-text">
                      <p>
                        The email address is not made public. It will only be used if you need to be contacted about your
                        account or for opted-in notifications.
                      </p>
                    </div>
                  </div>

                  <div className="col-lg-12">
                    <label>
                      Password <span>*</span>
                    </label>
                    <input 
                      type="password" 
                      className="form-control" 
                      placeholder="Enter password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="col-lg-12">
                    <label>
                      Confirm Password <span>*</span>
                    </label>
                    <input 
                      type="password" 
                      className="form-control" 
                      placeholder="Confirm password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      required
                    />
                    <div className="des-text">
                      <p>
                        Several special characters are allowed, including space, period (.), hyphen (-), apostrophe ('), underscore
                        (_), and the @ sign.
                      </p>
                      <p>
                        This question is for testing whether or not you are a human visitor and to prevent automated spam
                        submissions.
                      </p>
                    </div>
                  </div>

                  <div className="col-lg-12">
                    <button 
                      type="submit" 
                      className="btn btn-primary"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <i className="bi bi-arrow-clockwise spin"></i> Creating account...
                        </>
                      ) : (
                        <>
                          <img src="/images/icons/drop_down.svg" alt="" />
                          Create new account
                        </>
                      )}
                    </button>
                  </div>

                  <div className="col-lg-12 text-center mt-3">
                    <p>
                      Already have an account?{' '}
                      <Link href={`/${currentLang}/login`} className="text-primary">
                        Log in here
                      </Link>
                    </p>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

